import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PlayingCard from './PlayingCard';
import TableVacancyPrompt from './TableVacancyPrompt';
import { SEAT_LABELS, seatOccupantLabel } from '../utils/tableSeats';
import {
  useOwnHandMetrics,
} from '../utils/handLayout';
import {
  layoutGinHandGroups,
  layoutOppFacedown,
  layoutGinRevealHand,
  preferentialMeldsFromOrder,
  applyOneTimeRunSort,
  meldCardKey,
} from '../utils/ginHandLayout';

/**
 * Gin table:
 * - In-hand meld boxes (own mid-hand; opponent only on knock/gin reveal)
 * - Double-tap discard; tap-draw highlight until next action (not deal, not drag-draw)
 * - Drag from stock/discard inserts at drop index; melds recompute on drop
 * - Sticky meld packing (keep existing groups; least deadwood; hand-order can swap set/run)
 * - Host vacancy prompt when a seated player stands mid-game
 * - Single-select only (multi-select framework reserved for future Rummy)
 */

/** @reserved future Gin Rummy multi-meld submit */
const MAX_MELD_SELECT = 4;
void MAX_MELD_SELECT;
const FLY_MS = 420;
const DOUBLE_TAP_MS = 380;

function rectCenter(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

function GinCardStrip({ cards, width = 28, height = 40, className = '' }) {
  if (!cards?.length) return null;
  return (
    <div className={['gin-card-strip', className].filter(Boolean).join(' ')}>
      {cards.map((c) => (
        <PlayingCard key={c.id || `${c.rank}${c.suit}`} card={c} width={width} height={height} />
      ))}
    </div>
  );
}

function GinMeldStrip({ melds, width = 28, height = 40, tag }) {
  if (!melds?.length) return null;
  return (
    <div className="gin-reveal-melds">
      {melds.map((m, i) => (
        <div key={i} className={`gin-table-meld gin-table-meld--${m.type || 'set'}`}>
          {tag !== false && (
            <span className="gin-table-meld-tag">{m.type === 'run' ? 'Run' : 'Set'}</span>
          )}
          <GinCardStrip cards={m.cards} width={width} height={height} />
        </div>
      ))}
    </div>
  );
}

function GinTable({
  table,
  ginGameEvent = null,
  onStand,
  onHostAction,
  onGameAction,
  onSit,
}) {
  const game = table?.game;
  const mySeat = table?.viewerSeat;
  const isHost = !!table?.isHost;
  const gamePlaying = table?.status === 'playing' && !table?.gameOver;
  const phase = game?.phase;
  const meta = table?.myHandMeta;
  const serverHand = table?.myHand || [];
  const knockMax = game?.knockMax ?? table?.ginSettings?.knockMax ?? 10;
  const deadwood = meta?.deadwoodPoints;
  const handTarget = meta?.handTarget
    ?? game?.handTargets?.[mySeat]
    ?? 10;
  const canAct = gamePlaying && mySeat && (
    game?.turnSeat === mySeat
    || (phase === 'first_upcard' && game?.firstUpcardSeat === mySeat)
  );

  const ownMetrics = useOwnHandMetrics();
  const cardW = ownMetrics.w;
  const cardH = ownMetrics.h;
  const pileW = Math.round(cardW * 1.05);
  const pileH = Math.round(cardH * 1.05);
  const oppCardW = Math.max(24, Math.round(cardW * 0.48));
  const oppCardH = Math.max(34, Math.round(cardH * 0.48));

  const [handOrder, setHandOrder] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [drag, setDrag] = useState(null);
  const [warnMsg, setWarnMsg] = useState(null);
  const [availWidth, setAvailWidth] = useState(0);
  const [flyers, setFlyers] = useState([]);
  /** Display count lags server briefly so draws animate into hand */
  const [displayOppCount, setDisplayOppCount] = useState(0);
  const [revealAnim, setRevealAnim] = useState(false);
  /** Card drawn via tap from stock/discard (not deal, not drag) — highlight until action */
  const [freshDrawId, setFreshDrawId] = useState(null);

  const handRowRef = useRef(null);
  const stockRef = useRef(null);
  const discardRef = useRef(null);
  const oppHandRef = useRef(null);
  const ownHandRef = useRef(null);
  const reorderTimer = useRef(null);
  const warnTimer = useRef(null);
  const snapRef = useRef(null);
  const flyTimers = useRef([]);
  const lastTapRef = useRef({ id: null, t: 0 });
  /** True when next hand-size increase should NOT highlight (came from drag-drop) */
  const drawViaDragRef = useRef(false);
  /** Insert index for drag-draw from stock/discard (null = append) */
  const pendingInsertIndexRef = useRef(null);
  const prevHandIdsRef = useRef([]);
  /** Previous display melds — packing prefers keeping these when deadwood ties */
  const previousMeldsRef = useRef([]);
  /** Run card-set keys already one-time auto-sorted (player may reorganize after) */
  const sortedRunKeysRef = useRef(new Set());

  const showWarn = useCallback((msg) => {
    setWarnMsg(msg);
    if (warnTimer.current) clearTimeout(warnTimer.current);
    warnTimer.current = setTimeout(() => setWarnMsg(null), 2800);
  }, []);

  const emit = useCallback((action, payload) => onGameAction?.(action, payload), [onGameAction]);

  const pushReorder = useCallback((order) => {
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(() => {
      // Player order is authoritative after one-time run sort
      emit('gin-reorder', { cardIds: order });
    }, 180);
  }, [emit]);

  useEffect(() => () => {
    flyTimers.current.forEach((t) => clearTimeout(t));
  }, []);

  // Sync hand order from server; insert drag-draws at drop index; highlight tap-draws only
  useEffect(() => {
    const ids = serverHand.map((c) => c.id);
    const prevIds = prevHandIdsRef.current;
    const viaDrag = drawViaDragRef.current;
    const insertAt = pendingInsertIndexRef.current;
    let dragInsertedOrder = null;

    setHandOrder((prev) => {
      if (prev.length === ids.length && prev.every((id, i) => id === ids[i])) return prev;
      const serverSet = new Set(ids);
      const kept = prev.filter((id) => serverSet.has(id));
      const missing = ids.filter((id) => !kept.includes(id));
      if (!missing.length) return kept;
      // Drag from stock/discard: insert where dropped
      if (viaDrag && missing.length === 1 && insertAt != null) {
        const next = [...kept];
        const at = Math.max(0, Math.min(next.length, insertAt));
        next.splice(at, 0, missing[0]);
        dragInsertedOrder = next;
        return next;
      }
      return [...kept, ...missing];
    });
    setSelectedOrder((prev) => prev.filter((id) => ids.includes(id)));

    // Highlight only a single new card from tap-draw (not deal, not multi-add, not drag)
    if (ids.length === prevIds.length + 1 && prevIds.length > 0) {
      const prevSet = new Set(prevIds);
      const added = ids.find((id) => !prevSet.has(id));
      if (added) {
        if (viaDrag) setFreshDrawId(null);
        else setFreshDrawId(added);
      }
    } else if (ids.length <= prevIds.length) {
      setFreshDrawId((cur) => (cur && !ids.includes(cur) ? null : cur));
    } else {
      // Deal / full resync — never highlight
      setFreshDrawId(null);
    }

    if (dragInsertedOrder) {
      // Persist drop position; meld boxes recompute from orderedHand
      pushReorder(dragInsertedOrder);
    }

    drawViaDragRef.current = false;
    pendingInsertIndexRef.current = null;
    prevHandIdsRef.current = ids;
  }, [serverHand, pushReorder]);

  const handById = useMemo(() => {
    const m = new Map();
    for (const c of serverHand) m.set(c.id, c);
    return m;
  }, [serverHand]);

  const orderedHand = useMemo(
    () => handOrder.map((id) => handById.get(id)).filter(Boolean),
    [handOrder, handById],
  );

  // Display melds: least deadwood, keep existing groups, hand-order can swap set/run
  const inHandMelds = useMemo(
    () => preferentialMeldsFromOrder(orderedHand, {
      previousMelds: previousMeldsRef.current,
    }),
    [orderedHand],
  );

  useEffect(() => {
    previousMeldsRef.current = inHandMelds;
  }, [inHandMelds]);

  // Reset one-time run-sort + sticky melds on new deal
  useEffect(() => {
    sortedRunKeysRef.current = new Set();
    previousMeldsRef.current = [];
    setFreshDrawId(null);
  }, [game?.handNumber]);

  // One-time ascending sort when a new run is first detected — then player may reorganize
  useEffect(() => {
    if (!orderedHand.length || !handOrder.length) return;
    const melds = preferentialMeldsFromOrder(orderedHand, {
      previousMelds: previousMeldsRef.current,
    });
    const liveKeys = new Set(
      melds.filter((m) => m.type === 'run').map((m) => meldCardKey(m.cards)),
    );
    for (const k of [...sortedRunKeysRef.current]) {
      if (!liveKeys.has(k)) sortedRunKeysRef.current.delete(k);
    }
    const { order, newKeys } = applyOneTimeRunSort(
      handOrder,
      orderedHand,
      melds,
      sortedRunKeysRef.current,
    );
    if (!newKeys.length) return;
    newKeys.forEach((k) => sortedRunKeysRef.current.add(k));
    if (order.length === handOrder.length && order.every((id, i) => id === handOrder[i])) return;
    setHandOrder(order);
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(() => {
      emit('gin-reorder', { cardIds: order });
    }, 120);
    // handOrder intentionally omitted from deps after apply — only recheck when cards change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedHand, emit, game?.handNumber]);

  useEffect(() => {
    const el = handRowRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const felt = el.closest('.spades-felt') || el.parentElement;
      const w = felt?.clientWidth || el.clientWidth || 0;
      setAvailWidth(Math.max(0, w - 16));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const felt = el.closest('.spades-felt');
    if (felt) ro.observe(felt);
    return () => ro.disconnect();
  }, [cardW, mySeat, gamePlaying]);

  const handLayout = useMemo(
    () => layoutGinHandGroups(orderedHand, inHandMelds, {
      cardW,
      cardH,
      availWidth,
    }),
    [orderedHand, inHandMelds, cardW, cardH, availWidth],
  );

  const clearFreshDraw = useCallback((exceptId = null) => {
    setFreshDrawId((cur) => {
      if (!cur) return null;
      if (exceptId != null && cur === exceptId) return cur;
      return null;
    });
  }, []);

  const selectedIds = useMemo(() => new Set(selectedOrder), [selectedOrder]);

  // Single-select for discard / knock / gin (multi-select reserved for future Rummy)
  const selectedId = selectedOrder[0] || null;
  const primarySelected = selectedId ? handById.get(selectedId) : null;
  const handSizeOk = orderedHand.length === handTarget + 1;
  const canKnock = canAct && phase === 'turn_discard' && handSizeOk
    && deadwood != null && deadwood <= knockMax && deadwood > 0 && primarySelected;
  const canGin = canAct && phase === 'turn_discard' && handSizeOk
    && deadwood === 0 && primarySelected;
  const canBigGin = canAct && phase === 'turn_discard' && handSizeOk
    && !!meta?.isBigGin;
  const canDiscard = canAct && phase === 'turn_discard' && handSizeOk
    && !!primarySelected;

  const showFirstUp = canAct && phase === 'first_upcard';
  const showDraw = canAct && phase === 'turn_draw';

  const oppSeat = mySeat === 'north' ? 'south' : mySeat === 'south' ? 'north'
    : (table?.seats?.north ? 'south' : 'north');
  const serverOppCount = game?.handCounts?.[oppSeat] ?? 0;

  // Keep display count in sync when not animating a lag
  useEffect(() => {
    if (!gamePlaying) {
      setDisplayOppCount(serverOppCount);
      return;
    }
    // Snap up/down immediately if large jump (deal), else animations adjust
    if (Math.abs(serverOppCount - displayOppCount) > 2) {
      setDisplayOppCount(serverOppCount);
    }
  }, [serverOppCount, gamePlaying, displayOppCount]);

  const oppLayout = useMemo(
    () => layoutOppFacedown(displayOppCount, oppCardW, oppCardH),
    [displayOppCount, oppCardW, oppCardH],
  );

  const scoreLine = useMemo(() => {
    const sb = table?.scoreboard?.players || {};
    return ['north', 'south'].map((seat) => {
      const occ = table?.seats?.[seat];
      return {
        seat,
        name: seatOccupantLabel(seat, occ),
        score: sb[seat]?.score ?? 0,
      };
    });
  }, [table]);

  // Keep last-hand reveal on game over (live handResults or snapshot on gameOver)
  const handResults = table?.handResults || table?.gameOver?.lastHandResults || null;
  const isGameOver = !!table?.gameOver;
  const showResults = !!handResults && (
    isGameOver
    || (phase === 'hand_complete' && (gamePlaying || table?.status === 'playing'))
  );
  // Slightly larger reveal cards when results fill the table
  const revealCardW = Math.max(32, Math.round(cardW * 0.55));
  const revealCardH = Math.max(44, Math.round(cardH * 0.55));

  // Reveal animation when hand completes
  useEffect(() => {
    if (phase === 'hand_complete') {
      setRevealAnim(true);
      const t = setTimeout(() => setRevealAnim(false), 700);
      return () => clearTimeout(t);
    }
    setRevealAnim(false);
    return undefined;
  }, [phase, game?.handNumber]);

  const spawnFlyer = useCallback((opts) => {
    const {
      from, to, faceDown = true, card = null, w = oppCardW, h = oppCardH, onDone,
    } = opts;
    if (!from || !to) {
      onDone?.();
      return;
    }
    const id = `fly-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const flyer = {
      id,
      x: from.x,
      y: from.y,
      faceDown,
      card,
      w,
      h,
      flying: false,
    };
    setFlyers((f) => [...f, flyer]);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setFlyers((f) => f.map((fl) => (fl.id === id
          ? { ...fl, x: to.x, y: to.y, flying: true }
          : fl)));
      });
    });
    const t = setTimeout(() => {
      setFlyers((f) => f.filter((fl) => fl.id !== id));
      onDone?.();
    }, FLY_MS + 40);
    flyTimers.current.push(t);
  }, [oppCardW, oppCardH]);

  // Animate from state diffs + gin game events
  useEffect(() => {
    if (!gamePlaying || !game) {
      snapRef.current = null;
      return;
    }
    const counts = {
      north: game.handCounts?.north ?? 0,
      south: game.handCounts?.south ?? 0,
    };
    const snap = {
      counts,
      stock: game.stockCount ?? 0,
      discardId: game.topDiscard?.id ?? null,
      topDiscard: game.topDiscard || null,
      phase: game.phase,
      turn: game.turnSeat,
    };
    const prev = snapRef.current;
    snapRef.current = snap;
    if (!prev) {
      setDisplayOppCount(counts[oppSeat] ?? 0);
      return;
    }
    if (prev.phase === 'hand_complete' && snap.phase !== 'hand_complete') {
      setDisplayOppCount(counts[oppSeat] ?? 0);
      return;
    }

    const seats = ['north', 'south'];
    for (const seat of seats) {
      const was = prev.counts[seat] ?? 0;
      const now = counts[seat] ?? 0;
      if (now === was) continue;
      const isOpp = seat === oppSeat;
      const handEl = isOpp ? oppHandRef.current : ownHandRef.current;

      if (now > was) {
        // Draw into hand
        const fromStock = (snap.stock ?? 0) < (prev.stock ?? 0);
        const fromEl = fromStock ? stockRef.current : discardRef.current;
        const faceDown = isOpp || fromStock;
        const card = fromStock ? null : (prev.topDiscard || null);
        if (isOpp) {
          // Keep display count at previous until flyer lands
          setDisplayOppCount(was);
        }
        spawnFlyer({
          from: rectCenter(fromEl),
          to: rectCenter(handEl),
          faceDown,
          card,
          w: isOpp ? oppCardW : Math.round(cardW * 0.85),
          h: isOpp ? oppCardH : Math.round(cardH * 0.85),
          onDone: () => {
            if (isOpp) setDisplayOppCount(now);
          },
        });
      } else if (now < was) {
        // Discard / knock card leaving hand
        if (isOpp) setDisplayOppCount(was);
        spawnFlyer({
          from: rectCenter(handEl),
          to: rectCenter(discardRef.current),
          faceDown: isOpp && phase !== 'hand_complete',
          card: snap.topDiscard,
          w: isOpp ? oppCardW : Math.round(cardW * 0.85),
          h: isOpp ? oppCardH : Math.round(cardH * 0.85),
          onDone: () => {
            if (isOpp) setDisplayOppCount(now);
          },
        });
      }
    }
  }, [
    gamePlaying,
    game?.handCounts?.north,
    game?.handCounts?.south,
    game?.stockCount,
    game?.topDiscard?.id,
    game?.phase,
    game?.handNumber,
    oppSeat,
    spawnFlyer,
    oppCardW,
    oppCardH,
    cardW,
    cardH,
    phase,
  ]);

  // Extra pulse from explicit gin events (helps bot turns)
  useEffect(() => {
    if (!ginGameEvent || !gamePlaying) return;
    // State-diff handler already covers most; events can re-trigger if needed later
  }, [ginGameEvent, gamePlaying]);

  let hint = '';
  if (!mySeat) {
    hint = 'Sit North or South to play Gin (2 players).';
  } else if (phase === 'first_upcard') {
    hint = game?.firstUpcardSeat === mySeat
      ? 'Take the upcard or pass. Melds group in your hand automatically.'
      : 'Waiting for upcard decision…';
  } else if (phase === 'turn_draw') {
    hint = canAct
      ? 'Draw from stock or discard (tap or drag). Sets/runs stay in hand with a box.'
      : `Waiting for ${SEAT_LABELS[game?.turnSeat] || '…'}…`;
  } else if (phase === 'turn_discard') {
    hint = canAct
      ? (meta?.isBigGin
        ? 'Big gin available (all 11 cards melded) · or double-tap a card to discard. DW: 0'
        : `Double-tap a card to discard · Knock/Gin when ready. DW: ${deadwood ?? '—'}`)
      : `Waiting for ${SEAT_LABELS[game?.turnSeat] || '…'}…`;
  } else if (phase === 'hand_complete') {
    hint = 'Hand complete — hands revealed.';
  }

  /** Single-select toggle (Gin). Multi-select path kept disabled for Rummy later. */
  const toggleSelect = (cardId) => {
    clearFreshDraw(cardId);
    setSelectedOrder((prev) => (prev[0] === cardId ? [] : [cardId]));
  };

  const tryDiscard = useCallback((cardId = null) => {
    if (!canAct || phase !== 'turn_discard' || !handSizeOk) {
      if (canAct && phase === 'turn_discard' && !handSizeOk) {
        showWarn('Draw a card before discarding.');
      }
      return false;
    }
    if (cardId) {
      setFreshDrawId(null);
      emit('gin-discard', { cardId });
      setSelectedOrder([]);
      return true;
    }
    if (!selectedOrder[0]) {
      showWarn('Select a card to discard (double-tap, or drag to the pile).');
      return false;
    }
    setFreshDrawId(null);
    emit('gin-discard', { cardId: selectedOrder[0] });
    setSelectedOrder([]);
    return true;
  }, [canAct, phase, handSizeOk, selectedOrder, emit, showWarn]);

  const indexFromPointer = (clientX) => {
    const row = handRowRef.current;
    if (!row) return 0;
    const kids = [...row.querySelectorAll('[data-gin-card]')];
    if (!kids.length) return 0;
    for (let i = 0; i < kids.length; i += 1) {
      const r = kids[i].getBoundingClientRect();
      if (clientX < r.left + r.width / 2) return i;
    }
    return kids.length;
  };

  const onCardPointerDown = (e, card, index) => {
    if (!mySeat || !gamePlaying) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({
      cardId: card.id,
      index,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      moved: false,
      pointerId: e.pointerId,
      mode: 'hand',
    });
  };

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      if (e.pointerId !== drag.pointerId) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const moved = drag.moved || Math.hypot(dx, dy) > 6;
      setDrag((d) => (d ? {
        ...d, x: e.clientX, y: e.clientY, moved,
      } : null));

      if (moved && drag.mode === 'hand') {
        // Any drag clears "just drawn" highlight
        if (freshDrawId) setFreshDrawId(null);
        const insertAt = indexFromPointer(e.clientX);
        setHandOrder((order) => {
          const from = order.indexOf(drag.cardId);
          if (from < 0) return order;
          let to = insertAt;
          if (to > from) to -= 1;
          to = Math.max(0, Math.min(order.length - 1, to));
          if (to === from) return order;
          const next = [...order];
          const [item] = next.splice(from, 1);
          next.splice(to, 0, item);
          return next;
        });
      }
    };
    const onUp = (e) => {
      if (e.pointerId !== drag.pointerId) return;
      const discardZone = discardRef.current || document.getElementById('gin-drop-discard');
      const pt = { x: e.clientX, y: e.clientY };
      const inRect = (el) => {
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return pt.x >= r.left && pt.x <= r.right && pt.y >= r.top && pt.y <= r.bottom;
      };

      if (drag.moved && drag.mode === 'hand') {
        if (inRect(discardZone) && canAct && phase === 'turn_discard' && handSizeOk) {
          tryDiscard(drag.cardId);
        } else {
          setHandOrder((order) => {
            pushReorder(order);
            return order;
          });
        }
      } else if (drag.moved && (drag.mode === 'pile-stock' || drag.mode === 'pile-discard')) {
        const handZone = handRowRef.current;
        const canDropOnHand = showDraw
          || (showFirstUp && drag.mode === 'pile-discard');
        if (inRect(handZone) && canDropOnHand) {
          // Insert at pointer position among current hand cards
          drawViaDragRef.current = true;
          pendingInsertIndexRef.current = indexFromPointer(e.clientX);
          if (showFirstUp && drag.mode === 'pile-discard') {
            emit('gin-first-upcard', { choice: 'take' });
          } else {
            emit('gin-draw', {
              source: drag.mode === 'pile-stock' ? 'stock' : 'discard',
            });
          }
        }
      } else if (!drag.moved && drag.mode === 'hand') {
        // Double-tap same card on discard stage → discard that card
        const now = Date.now();
        const last = lastTapRef.current;
        if (
          phase === 'turn_discard'
          && handSizeOk
          && canAct
          && last.id === drag.cardId
          && (now - last.t) < DOUBLE_TAP_MS
        ) {
          lastTapRef.current = { id: null, t: 0 };
          tryDiscard(drag.cardId);
        } else {
          lastTapRef.current = { id: drag.cardId, t: now };
          // Clicking a different card clears draw highlight
          if (freshDrawId && freshDrawId !== drag.cardId) {
            setFreshDrawId(null);
          }
          toggleSelect(drag.cardId);
        }
      } else if (!drag.moved && drag.mode === 'pile-stock' && showDraw) {
        drawViaDragRef.current = false;
        emit('gin-draw', { source: 'stock' });
      } else if (!drag.moved && drag.mode === 'pile-discard') {
        drawViaDragRef.current = false;
        if (showFirstUp) emit('gin-first-upcard', { choice: 'take' });
        else if (showDraw) emit('gin-draw', { source: 'discard' });
      }
      setDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [
    drag, canAct, phase, handSizeOk, emit, pushReorder, showDraw, showFirstUp,
    tryDiscard, handById, freshDrawId,
  ]);

  const onPilePointerDown = (e, mode) => {
    if (e.button !== 0) return;
    if (mode === 'pile-stock' && !showDraw) return;
    if (mode === 'pile-discard' && !showDraw && !showFirstUp) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({
      cardId: mode === 'pile-discard' && game?.topDiscard ? game.topDiscard.id : null,
      pileCard: mode === 'pile-discard' ? game?.topDiscard : null,
      index: -1,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      moved: false,
      pointerId: e.pointerId,
      mode,
    });
  };

  const dragCard = drag?.mode === 'hand' && drag?.cardId
    ? handById.get(drag.cardId)
    : drag?.mode === 'pile-discard'
      ? (drag.pileCard || game?.topDiscard)
      : null;
  const dragFaceDown = drag?.mode === 'pile-stock';

  // Build reveal hand for opponent (face-up, grouped) from handResults
  const revealOpp = useMemo(() => {
    if (!showResults || !handResults || handResults.draw) return null;
    const seat = oppSeat;
    const rh = handResults.revealedHands?.[seat];
    const melds = rh?.melds
      || (seat === handResults.knockerSeat
        ? handResults.knockerMelds
        : handResults.defenderMelds)
      || [];
    const dead = rh?.deadwood
      || (seat === handResults.knockerSeat
        ? handResults.knockerDeadwoodCards
        : handResults.defenderDeadwoodCards)
      || [];
    // Flatten for layout: meld cards then deadwood
    const cards = [];
    for (const m of melds) {
      for (const c of m.cards || []) cards.push(c);
    }
    for (const c of dead) {
      if (!cards.some((x) => x.id === c.id)) cards.push(c);
    }
    const layout = layoutGinRevealHand(cards, melds, oppCardW, oppCardH);
    return { melds, dead, cards, layout, layoffs: rh?.layoffs || handResults.layoffs || [] };
  }, [showResults, handResults, oppSeat, oppCardW, oppCardH]);

  const showOppFacedown = gamePlaying && phase !== 'hand_complete' && displayOppCount > 0;

  // Host vacancy (player stood mid-game) — same flow as Spades/Hearts
  const showVacancyPrompt = isHost && gamePlaying && !table?.gameOver
    && !!table?.siteBotsEnabled
    && (table?.hostVacancyPrompt?.seats?.length > 0);

  return (
    <div className="table-view table-view--gin table-view--gin-body">
      <div className="table-view-hint-slot" aria-live="polite">
        <p className="table-view-hint">{hint || '\u00A0'}</p>
      </div>
      {warnMsg && (
        <div className="gin-warn-toast" role="status" aria-live="assertive">
          {warnMsg}
        </div>
      )}

      <div className="spades-table-wrap gin-table-outer">
        <div className="spades-felt gin-felt">
          <div
            className={[
              'spades-table-overlay',
              showVacancyPrompt ? 'spades-table-overlay--host' : '',
            ].filter(Boolean).join(' ')}
          >
            <TableVacancyPrompt
              prompt={showVacancyPrompt ? table.hostVacancyPrompt : null}
              floating
              onFillBots={() => onHostAction?.('fill-vacant-seats')}
              onWaitForPlayer={() => onHostAction?.('wait-vacant-seats')}
            />
          </div>
          <div className="gin-score-bar">
            {scoreLine.map((row) => (
              <div key={row.seat} className="gin-score-chip">
                <span>{row.name}</span>
                <strong>{row.score}</strong>
              </div>
            ))}
            <div className="gin-score-chip gin-score-chip--meta">
              Hand {game?.handNumber || 0}
              {' · '}
              knock ≤{knockMax}
              {deadwood != null ? ` · DW ${deadwood}` : ''}
            </div>
          </div>

          {/* Opponent seat — face-down hand (actions animate here) or reveal */}
          <div className="gin-opp-row">
            {['north', 'south'].filter((s) => !mySeat || s !== mySeat).map((seat) => {
              const occ = table.seats?.[seat];
              const isThisOpp = seat === oppSeat;
              return (
                <div key={seat} className="gin-opp-seat">
                  <span className="gin-opp-name">{seatOccupantLabel(seat, occ)}</span>

                  {isThisOpp && showOppFacedown && (
                    <div
                      ref={oppHandRef}
                      className="gin-opp-hand gin-opp-hand--facedown gin-opp-hand--anim"
                      aria-label={`${displayOppCount} cards in hand`}
                      style={{
                        width: `${oppLayout.width}px`,
                        height: `${oppLayout.height}px`,
                      }}
                    >
                      {Array.from({ length: displayOppCount }, (_, i) => (
                        <div
                          key={i}
                          className="gin-opp-hand-card"
                          style={{
                            left: `${i * oppLayout.step}px`,
                            zIndex: i + 1,
                            width: `${oppCardW}px`,
                            height: `${oppCardH}px`,
                          }}
                        >
                          <PlayingCard faceDown width={oppCardW} height={oppCardH} />
                        </div>
                      ))}
                    </div>
                  )}

                  {isThisOpp && showResults && revealOpp && (
                    <div
                      className={[
                        'gin-opp-hand',
                        'gin-opp-hand--reveal',
                        revealAnim ? 'gin-opp-hand--reveal-in' : '',
                      ].filter(Boolean).join(' ')}
                      style={{
                        width: `${revealOpp.layout.boxW}px`,
                        height: `${revealOpp.layout.boxH}px`,
                      }}
                      aria-label="Opponent hand revealed"
                    >
                      {revealOpp.layout.groupBoxes.map((box) => (
                        <div
                          key={box.key}
                          className={`gin-hand-meld-box gin-hand-meld-box--${box.type}`}
                          style={{
                            left: `${box.left}px`,
                            top: `${box.top}px`,
                            width: `${box.width}px`,
                            height: `${box.height}px`,
                          }}
                        />
                      ))}
                      {revealOpp.cards.map((c) => {
                        const left = revealOpp.layout.positions.get(c.id) ?? 0;
                        return (
                          <div
                            key={c.id}
                            className="gin-opp-hand-card gin-opp-hand-card--faceup"
                            style={{
                              left: `${left}px`,
                              top: 0,
                              zIndex: 10,
                              width: `${oppCardW}px`,
                              height: `${oppCardH}px`,
                            }}
                          >
                            <PlayingCard card={c} width={oppCardW} height={oppCardH} />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!mySeat && (
                    <button type="button" className="table-action-btn table-action-btn--small" onClick={() => onSit?.(seat)}>
                      Sit {SEAT_LABELS[seat]}
                    </button>
                  )}
                </div>
              );
            })}
            {!mySeat && !table.seats?.north && (
              <button type="button" className="table-action-btn" onClick={() => onSit?.('north')}>Sit North</button>
            )}
            {!mySeat && !table.seats?.south && (
              <button type="button" className="table-action-btn" onClick={() => onSit?.('south')}>Sit South</button>
            )}
          </div>

          {/* Center piles */}
          <div className="gin-center">
            <div
              ref={stockRef}
              id="gin-drop-stock"
              className={[
                'gin-pile',
                'gin-pile--stock',
                !showDraw ? 'gin-pile--disabled' : '',
              ].filter(Boolean).join(' ')}
              onPointerDown={(e) => onPilePointerDown(e, 'pile-stock')}
              title="Draw from stock (tap or drag onto hand)"
              role="button"
              tabIndex={showDraw ? 0 : -1}
            >
              <PlayingCard faceDown width={pileW} height={pileH} />
              <span className="gin-pile-label">Stock ({game?.stockCount ?? 0})</span>
            </div>
            <div
              ref={discardRef}
              id="gin-drop-discard"
              className={[
                'gin-pile',
                'gin-pile--discard',
                !showDraw && !showFirstUp && !canDiscard ? 'gin-pile--disabled' : '',
              ].filter(Boolean).join(' ')}
              onPointerDown={(e) => onPilePointerDown(e, 'pile-discard')}
              title="Discard pile"
              role="button"
              tabIndex={0}
            >
              {game?.topDiscard ? (
                <PlayingCard card={game.topDiscard} width={pileW} height={pileH} />
              ) : (
                <PlayingCard faceDown width={pileW} height={pileH} className="gin-pile-empty" />
              )}
              <span className="gin-pile-label">Discard</span>
            </div>
          </div>

          {showFirstUp && (
            <div className="gin-action-bar">
              <button
                type="button"
                className="spades-bid-btn spades-bid-btn--primary"
                onClick={() => {
                  drawViaDragRef.current = false;
                  emit('gin-first-upcard', { choice: 'take' });
                }}
              >
                Take upcard
              </button>
              <button type="button" className="spades-bid-btn" onClick={() => emit('gin-first-upcard', { choice: 'pass' })}>
                Pass
              </button>
            </div>
          )}

          {/* Own hand — melds boxed & grouped tighter in-hand */}
          {mySeat && gamePlaying && phase !== 'hand_complete' && (
            <div
              className={[
                'gin-hand',
                'gin-hand--spread',
                'gin-hand--grouped',
                drag?.moved ? 'gin-hand--dragging' : '',
              ].filter(Boolean).join(' ')}
              ref={(node) => {
                handRowRef.current = node;
                ownHandRef.current = node;
              }}
              id="gin-drop-hand"
            >
              <div
                className="gin-hand-cards"
                style={{
                  width: `${handLayout.boxW}px`,
                  height: `${handLayout.boxH}px`,
                }}
              >
                {handLayout.groupBoxes.map((box) => (
                  <div
                    key={box.key}
                    className={`gin-hand-meld-box gin-hand-meld-box--${box.type}`}
                    style={{
                      left: `${box.left}px`,
                      top: `${box.top}px`,
                      width: `${box.width}px`,
                      height: `${box.height}px`,
                    }}
                    aria-hidden
                  />
                ))}
                {orderedHand.map((c, index) => {
                  const isDragging = drag?.mode === 'hand' && drag?.cardId === c.id && drag.moved;
                  const left = handLayout.positions.get(c.id) ?? 0;
                  const isFresh = freshDrawId === c.id;
                  const inPreferred = inHandMelds.some((m) => (m.cards || []).some((x) => x.id === c.id));
                  return (
                    <div
                      key={c.id}
                      data-gin-card={c.id}
                      className={[
                        'gin-hand-card-btn',
                        selectedIds.has(c.id) ? 'gin-hand-card-btn--selected' : '',
                        inPreferred ? 'gin-hand-card-btn--meld' : '',
                        isFresh ? 'gin-hand-card-btn--fresh' : '',
                        isDragging ? 'gin-hand-card-btn--ghost' : '',
                      ].filter(Boolean).join(' ')}
                      onPointerDown={(e) => onCardPointerDown(e, c, index)}
                      style={{
                        left: `${left}px`,
                        top: 0,
                        zIndex: index + 10,
                        width: `${cardW}px`,
                        height: `${cardH}px`,
                        touchAction: 'none',
                      }}
                    >
                      <PlayingCard
                        card={c}
                        width={cardW}
                        height={cardH}
                        /* Fresh draw: highlight only — keep normal hand z/position (no raise) */
                        raised={selectedIds.has(c.id)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Flying cards for draw/discard animations */}
          {flyers.map((fl) => (
            <div
              key={fl.id}
              className={['gin-flyer', fl.flying ? 'gin-flyer--moving' : ''].filter(Boolean).join(' ')}
              style={{
                left: fl.x,
                top: fl.y,
                width: fl.w,
                height: fl.h,
                transitionDuration: `${FLY_MS}ms`,
              }}
            >
              {fl.faceDown || !fl.card ? (
                <PlayingCard faceDown width={fl.w} height={fl.h} />
              ) : (
                <PlayingCard card={fl.card} width={fl.w} height={fl.h} />
              )}
            </div>
          ))}

          {drag?.moved && (dragCard || dragFaceDown) && (
            <div
              className="gin-drag-ghost"
              style={{ left: drag.x, top: drag.y }}
            >
              {dragFaceDown ? (
                <PlayingCard faceDown width={cardW} height={cardH} raised />
              ) : (
                <PlayingCard card={dragCard} width={cardW} height={cardH} raised />
              )}
            </div>
          )}

          <div className="gin-action-bar">
            {canAct && phase === 'turn_discard' && (
              <>
                <button
                  type="button"
                  className="spades-bid-btn spades-bid-btn--primary"
                  disabled={!handSizeOk || !primarySelected}
                  onClick={() => {
                    if (!primarySelected) {
                      showWarn('Select a card to discard (or double-tap it).');
                      return;
                    }
                    tryDiscard();
                  }}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="spades-bid-btn"
                  disabled={!canKnock}
                  onClick={() => {
                    if (!primarySelected) {
                      showWarn('Select the card to discard when knocking.');
                      return;
                    }
                    emit('gin-knock', { cardId: primarySelected.id });
                    setSelectedOrder([]);
                  }}
                >
                  Knock
                </button>
                <button
                  type="button"
                  className="spades-bid-btn spades-bid-btn--primary"
                  disabled={!canGin}
                  onClick={() => {
                    if (!primarySelected) {
                      showWarn('Select the card to discard when going gin.');
                      return;
                    }
                    emit('gin-gin', { cardId: primarySelected.id });
                    setSelectedOrder([]);
                  }}
                >
                  Gin
                </button>
                {canBigGin && (
                  <button
                    type="button"
                    className="spades-bid-btn spades-bid-btn--primary"
                    title="All 11 cards in melds — no discard"
                    onClick={() => {
                      emit('gin-big-gin', {});
                      setSelectedOrder([]);
                    }}
                  >
                    Big gin
                  </button>
                )}
              </>
            )}
            {selectedId && (
              <button type="button" className="spades-bid-btn" onClick={() => setSelectedOrder([])}>
                Clear select
              </button>
            )}
          </div>

          {isHost && !gamePlaying && !table.gameOver && !table.needsGamePick && (
            <div className="gin-action-bar">
              <button type="button" className="spades-bid-btn spades-bid-btn--primary" onClick={() => onHostAction?.('start-game')}>
                Start Gin
              </button>
            </div>
          )}

        </div>

        {/* Full-table results overlay (hand complete + game over with last hand) */}
        {(showResults || isGameOver) && (
          <div
            className={[
              'gin-results-overlay',
              isGameOver ? 'gin-results-overlay--game-over' : '',
            ].filter(Boolean).join(' ')}
            role="dialog"
            aria-modal="true"
            aria-label={isGameOver ? 'Game over' : 'Hand results'}
          >
            <div className="gin-hand-results gin-hand-results--reveal gin-hand-results--full">
              {isGameOver && (
                <p className="gin-results-game-over-title">
                  Game over —
                  {' '}
                  <strong>{table.gameOver.winnerLabel || table.gameOver.winner}</strong>
                  {' '}
                  wins!
                </p>
              )}

              {showResults && (
                <>
                  <p className="spades-bid-prompt">
                    {handResults.draw
                      ? 'Hand drawn — stock empty'
                      : handResults.isBigGin
                        ? `${seatOccupantLabel(handResults.knockerSeat, table.seats?.[handResults.knockerSeat])} scored big gin!`
                        : handResults.isGin
                          ? `${seatOccupantLabel(handResults.knockerSeat, table.seats?.[handResults.knockerSeat])} went gin!`
                          : handResults.undercut
                            ? `Undercut! ${seatOccupantLabel(
                              handResults.defenderSeat
                                || (handResults.knockerSeat === 'north' ? 'south' : 'north'),
                              table.seats?.[
                                handResults.defenderSeat
                                  || (handResults.knockerSeat === 'north' ? 'south' : 'north')
                              ],
                            )} wins the undercut`
                            : `${seatOccupantLabel(handResults.knockerSeat, table.seats?.[handResults.knockerSeat])} knocked`}
                    {handResults.handNumber != null ? ` · Hand ${handResults.handNumber}` : ''}
                  </p>

                  {!handResults.draw && (
                    <div className="gin-reveal-body">
                      <div className="gin-reveal-seat">
                        <div className="gin-reveal-seat-title">
                          {seatOccupantLabel(handResults.knockerSeat, table.seats?.[handResults.knockerSeat])}
                          {handResults.isBigGin ? ' · Big gin' : handResults.isGin ? ' · Gin' : ' · Knock'}
                          {handResults.knockerDeadwood != null ? ` · DW ${handResults.knockerDeadwood}` : ''}
                        </div>
                        {handResults.discard && (
                          <div className="gin-reveal-row">
                            <span className="gin-reveal-label">Discard</span>
                            <GinCardStrip
                              cards={[handResults.discard]}
                              width={revealCardW}
                              height={revealCardH}
                            />
                          </div>
                        )}
                        {handResults.isBigGin && !handResults.discard && (
                          <p className="gin-reveal-note">No discard — all 11 cards melded</p>
                        )}
                        {(handResults.knockerMelds || []).length > 0 && (
                          <div className="gin-reveal-row">
                            <span className="gin-reveal-label">
                              Melds
                              {(handResults.layoffs || []).length > 0 ? ' (+layoffs)' : ''}
                            </span>
                            <GinMeldStrip
                              melds={handResults.knockerMelds}
                              width={revealCardW}
                              height={revealCardH}
                            />
                          </div>
                        )}
                        {(handResults.knockerDeadwoodCards || []).length > 0 && (
                          <div className="gin-reveal-row">
                            <span className="gin-reveal-label">Deadwood</span>
                            <GinCardStrip
                              cards={handResults.knockerDeadwoodCards}
                              width={revealCardW}
                              height={revealCardH}
                            />
                          </div>
                        )}
                      </div>

                      <div className="gin-reveal-seat">
                        <div className="gin-reveal-seat-title">
                          {seatOccupantLabel(
                            handResults.defenderSeat
                              || (handResults.knockerSeat === 'north' ? 'south' : 'north'),
                            table.seats?.[
                              handResults.defenderSeat
                                || (handResults.knockerSeat === 'north' ? 'south' : 'north')
                            ],
                          )}
                          {' · Defend'}
                          {handResults.defenderDeadwood != null ? ` · DW ${handResults.defenderDeadwood}` : ''}
                        </div>
                        {(handResults.defenderMelds || []).length > 0 && (
                          <div className="gin-reveal-row">
                            <span className="gin-reveal-label">Melds</span>
                            <GinMeldStrip
                              melds={handResults.defenderMelds}
                              width={revealCardW}
                              height={revealCardH}
                            />
                          </div>
                        )}
                        {(handResults.layoffs || []).length > 0 && (
                          <div className="gin-reveal-row">
                            <span className="gin-reveal-label">Laid off</span>
                            <GinCardStrip
                              cards={handResults.layoffs}
                              width={revealCardW}
                              height={revealCardH}
                              className="gin-card-strip--layoff"
                            />
                          </div>
                        )}
                        {(handResults.defenderDeadwoodCards || []).length > 0 && (
                          <div className="gin-reveal-row">
                            <span className="gin-reveal-label">Deadwood</span>
                            <GinCardStrip
                              cards={handResults.defenderDeadwoodCards}
                              width={revealCardW}
                              height={revealCardH}
                            />
                          </div>
                        )}
                        {handResults.isGin && (
                          <p className="gin-reveal-note">No layoffs on gin</p>
                        )}
                      </div>
                    </div>
                  )}

                  <ul className="gin-results-list">
                    {['north', 'south'].map((seat) => {
                      const ps = handResults.players?.[seat];
                      const finalPs = isGameOver ? table.gameOver?.players?.[seat] : null;
                      const score = finalPs?.score ?? ps?.score;
                      if (ps == null && score == null) return null;
                      return (
                        <li key={seat}>
                          <span>{seatOccupantLabel(seat, table.seats?.[seat])}</span>
                          <span>
                            {ps != null && (
                              <>
                                {ps.delta > 0 ? `+${ps.delta}` : ps.delta}
                                {' → '}
                              </>
                            )}
                            <strong>{score ?? 0}</strong>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              {/* Game over without hand snapshot still shows final scores */}
              {isGameOver && !showResults && (
                <ul className="gin-results-list">
                  {['north', 'south'].map((seat) => {
                    const ps = table.gameOver?.players?.[seat];
                    if (!ps) return null;
                    return (
                      <li key={seat}>
                        <span>{seatOccupantLabel(seat, table.seats?.[seat])}</span>
                        <strong>{ps.score ?? 0}</strong>
                      </li>
                    );
                  })}
                </ul>
              )}

              {isGameOver && table.gameOver?.hand != null && (
                <p className="gin-results-meta">Final · after hand {table.gameOver.hand}</p>
              )}

              <div className="gin-results-actions">
                {!isGameOver && isHost && (
                  <button
                    type="button"
                    className="spades-bid-btn spades-bid-btn--primary"
                    onClick={() => onHostAction?.('next-hand')}
                  >
                    Next hand
                  </button>
                )}
                {isGameOver && (
                  <>
                    <button
                      type="button"
                      className="spades-bid-btn"
                      onClick={() => onHostAction?.('dismiss-results')}
                    >
                      Close
                    </button>
                    {isHost && (
                      <button
                        type="button"
                        className="spades-bid-btn spades-bid-btn--primary"
                        onClick={() => onHostAction?.('new-game')}
                      >
                        New game
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GinTable;
